/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React from "react";
import { Box, Tooltip } from "@mui/material";
import { IRemoteEquipment } from "../../store/api/devices/types";

interface TopologyPathSegment {
  label: string;
  type: string;
  serialNumber?: string;
  status?: string;
  hostname?: string;
  objectKey?: string;
}

interface DeviceTopologyPathProps {
  device: IRemoteEquipment;
  parsedAttributes: Record<string, any>;
  chassisTypeName?: string | null;
  allEquipment?: IRemoteEquipment[];
  selectedObjectKey?: string;
  onSegmentClick?: (objectKey: string) => void;
}

type PlatformType = 'ma3000' | 'ma6200g1' | 'ma6200g2' | 'ma6000g2' | 'unknown';

/**
 * DeviceTopologyPath Component
 * Displays the topology connection path for remote devices in graph-style boxes
 * Example: IHU -> OIM(slot) -> RAU(port) -> RXU
 */
const DeviceTopologyPath: React.FC<DeviceTopologyPathProps> = ({
  device,
  parsedAttributes,
  chassisTypeName,
  allEquipment = [],
  selectedObjectKey,
  onSegmentClick,
}) => {
  // ==============================
  // Shared helpers
  // ==============================

  /**
   * Get status color based on device status
   */
  const getStatusColor = (status?: string): string => {
    if (!status) return "#9ca3af"; // gray-400
    const statusLower = status.toLowerCase();
    
    if (statusLower.includes("clear") || statusLower.includes("online")) return "#22c55e"; // green-500
    if (statusLower.includes("minor")) return "#eab308"; // yellow-500
    if (statusLower.includes("major")) return "#f97316"; // orange-500
    if (statusLower.includes("critical")) return "#ef4444"; // red-500
    if (statusLower.includes("disconnect")) return "#6b7280"; // gray-500
    
    return "#9ca3af"; // gray-400 (unknown)
  };

  /**
   * Detect device platform based on objectKey format
   * MA3000: dot-separated (e.g., sim1A2345.rxu.5.8.3.1)
   * MA6200G1: underscore-separated (e.g., SN123ABC_0_5.2.3.0)
   */
  const detectPlatform = (): PlatformType => {
    const model = (device.model || '').toUpperCase();
    const type = (device.equipmentType || '').toUpperCase();
    
    if ( model.includes('MA6200-G2') ||  model.includes('MA6200G2') || ['A3', 'E3', 'M3','N3','X3'].some(t => type.includes(t))) {
      return 'ma6200g2';
    }
    if (model.includes('MA6200-G1') ||  model.includes('MA6200G1') || ['A2', 'E2', 'M2','N2','X2'].some(t => type.includes(t))) {
      return 'ma6200g1';
    }
    if (model.includes('3000') || ['HCM', 'ACM', 'RIM', 'OIM', 'RAU', 'RXU', 'MRU', 'MXU','GX','OCH','FCM', 'RXU67'].some(t => type.includes(t))) {
      return 'ma3000';
    }
    if (model.includes('MA6000-G2') ||  model.includes('MA6000G2') || ['DCU', 'RIU', 'DEU', 'DXRU', 'DXRU10G', 'PSU', 'SPSU', 'S-PSU'].some(t => type.includes(t))) {
      return 'ma6000g2';
    }
    return 'unknown';
  };

  // ==============================
  // MA6200 shared helpers (G1/G2)
  // ==============================

  /**
   * Build a node label matching the topology graph format: {equipmentType}.{port}
   * Root (primary A2/A3) uses platform label: "MA6200-G1" or "MA6200-G2"
   */
  const buildMa6200NodeLabel = (
    equipmentType: string,
    port: string,
    isRoot: boolean,
    platform: 'g1' | 'g2'
  ): string => {
    if (isRoot) {
      return platform === 'g2' ? 'MA6200-G2' : 'MA6200-G1';
    }
    return equipmentType ? `${equipmentType}.${port}` : `DEVICE.${port}`;
  };

  const normalizeRoute = (route?: string): string => {
    const raw = (route || '').trim();
    if (!raw) return '';
    if (raw === '0') return '0.0.0.0';
    if (/^\d+$/.test(raw)) return `${raw}.0.0.0`;

    const parts = raw.split('.');
    if (parts.every((p) => /^\d+$/.test(p)) && parts.length < 4) {
      return [...parts, ...Array(4 - parts.length).fill('0')].join('.');
    }

    return raw;
  };

  const getTargetSerialFromObjectKey = (objectKey?: string): string => {
    const raw = (objectKey || '').trim();
    if (!raw) return '';

    // MA6200 underscore format: {targetSerial}_{devId}_{route}
    // Route itself contains dots, so underscore parsing must happen first.
    if (raw.includes('_')) {
      return raw.split('_')[0];
    }

    // Dot-separated formats (e.g., MA3000/MA6000 style object keys)
    if (raw.includes('.')) {
      return raw.split('.')[0];
    }

    return raw;
  };

  const belongsToTargetSerial = (eq: IRemoteEquipment, targetSerial: string): boolean => {
    if (!eq.objectKey || !targetSerial) return false;
    return eq.objectKey.startsWith(`${targetSerial}.`) || eq.objectKey.startsWith(`${targetSerial}_`);
  };

  const parseDxruTopology = (topology?: string): { deuKey: string; portPart: string } => {
    const raw = (topology || '').trim();
    if (!raw) return { deuKey: '', portPart: '' };

    const slashIndex = raw.indexOf('/');
    if (slashIndex === -1) return { deuKey: '', portPart: raw };

    return {
      deuKey: raw.slice(0, slashIndex).trim(),
      portPart: raw.slice(slashIndex + 1).trim(),
    };
  };

  const normalizeDeuDisplay = (topology?: string): string => {
    const raw = (topology || '').trim();
    if (!raw) return '0';
    return raw.replace(/^DEU\s*/i, '').trim() || '0';
  };

  const normalizeRiuDisplay = (topology?: string): string => {
    const raw = (topology || '').trim();
    if (!raw) return '0';
    return raw.replace(/^RIU\s*/i, '').trim() || '0';
  };

  const normalizePsuDisplay = (topology?: string): string => {
    const raw = (topology || '').trim();
    if (!raw) return '0';
    return raw.replace(/^S?PSU\s*/i, '').trim() || '0';
  };

  // ==============================
  // MA6200-G2
  // ==============================

  const buildMa6200G2NodeLabel = (eq: IRemoteEquipment, isRoot: boolean = false): string => {
    if (isRoot) return 'MA-6200G2';

    const topology = (eq.topology || '').trim();

    const route = normalizeRoute(topology);
    if (route) {
      const routeParts = route.split('.');
      for (let i = routeParts.length - 1; i >= 0; i--) {
        if (routeParts[i] !== '0') {
          return `${eq.equipmentType || 'DEVICE'}.${routeParts[i]}`;
        }
      }
    }

    return eq.equipmentType || 'Unknown';
  };

  const findMa6200G2Root = (targetSerial: string): IRemoteEquipment | undefined => {
    return allEquipment.find((eq) => {
      if (!belongsToTargetSerial(eq, targetSerial)) return false;
      const type = (eq.equipmentType || '').toUpperCase();
      if (!type.includes('A3')) return false;
      const route = normalizeRoute(eq.topology);
      return route === '0.0.0.0' || route === '';
    });
  };

  const parseMA6200G2Path = (): TopologyPathSegment[] => {
    const path: TopologyPathSegment[] = [];
    const pushUniqueSegment = (segment: TopologyPathSegment) => {
      if (!segment.objectKey) {
        path.push(segment);
        return;
      }

      const exists = path.some(
        (existing) => !!existing.objectKey && existing.objectKey === segment.objectKey
      );

      if (!exists) {
        path.push(segment);
      }
    };

    const objectKey = device.objectKey;
    if (!objectKey) return path;

    const targetSerial = getTargetSerialFromObjectKey(objectKey);
    if (!targetSerial) return path;

    const rootDevice = findMa6200G2Root(targetSerial);
    pushUniqueSegment({
      label: buildMa6200G2NodeLabel(rootDevice || device, true),
      type: rootDevice?.equipmentType || 'A3',
      serialNumber: rootDevice?.serialNumber || targetSerial,
      hostname: rootDevice?.hostname,
      status: rootDevice?.status || device.status,
      objectKey: rootDevice?.objectKey,
    });

    const currentType = (device.equipmentType || '').toUpperCase();
    const currentTopology = (device.topology || '').trim();
    const appendCurrentDevice = () => {
      pushUniqueSegment({
        label: buildMa6200G2NodeLabel(device),
        type: device.equipmentType || 'Device',
        serialNumber: device.serialNumber,
        hostname: device.hostname,
        status: device.status,
        objectKey: device.objectKey,
      });
    };

    if (currentType.includes('A3')) {
      return path;
    }

    const route = normalizeRoute(currentTopology);
    const routeOctets = route.split('.');
    if (routeOctets.length !== 4) {
      appendCurrentDevice();
      return path;
    }

    let currentRoute = '0.0.0.0';
    let currentDeviceAppended = false;

    for (let i = 0; i < 4; i++) {
      const octet = routeOctets[i];
      if (octet === '0') continue;

      const routeParts = currentRoute.split('.');
      routeParts[i] = octet;
      currentRoute = routeParts.join('.');

      const isCurrentDevice = currentRoute === route;

      if (isCurrentDevice) {
        appendCurrentDevice();
        currentDeviceAppended = true;
      } else {
        const intermediateByTopology = allEquipment.find((eq) =>
          belongsToTargetSerial(eq, targetSerial) && normalizeRoute(eq.topology) === currentRoute
        );

        const intermediateByObjectKey = allEquipment.find((eq) =>
          belongsToTargetSerial(eq, targetSerial)
          && !!eq.objectKey
          && (
            eq.objectKey.endsWith(`_${currentRoute}`)
            || eq.objectKey.endsWith(`.${currentRoute}`)
          )
        );

        const intermediateDevice = intermediateByTopology || intermediateByObjectKey;

        if (intermediateDevice) {
          pushUniqueSegment({
            label: buildMa6200G2NodeLabel(intermediateDevice),
            type: intermediateDevice.equipmentType || 'Device',
            serialNumber: intermediateDevice.serialNumber,
            hostname: intermediateDevice.hostname,
            status: intermediateDevice.status || device.status,
            objectKey: intermediateDevice.objectKey,
          });
        }
      }
    }

    if (!currentDeviceAppended) {
      appendCurrentDevice();
    }

    return path;
  };

  // ==============================
  // MA6000-G2
  // ==============================

  const buildMa6000G2NodeLabel = (eq: IRemoteEquipment, isRoot: boolean = false): string => {
    if (isRoot) return 'MA-6000G2';

    const equipmentType = (eq.equipmentType || '').toUpperCase();
    const topology = (eq.topology || '').trim();

    if (equipmentType.includes('SDCU') || equipmentType.includes('S-DCU')) {
      return 'sDCU';
    }

    if (equipmentType.includes('SRIU') || equipmentType.includes('S-RIU')) {
      return `sRIU-${normalizeRiuDisplay(topology)}`;
    }

    if (equipmentType.includes('SPSU') || equipmentType.includes('S-PSU')) {
      return `sPSU-${normalizePsuDisplay(topology)}`;
    }

    if (equipmentType.includes('DXRU')) {
      const { portPart } = parseDxruTopology(topology);
      const baseLabel = equipmentType.includes('10G') ? 'DxRU10G' : 'DxRU';
      return `${baseLabel} ${portPart || topology || '0'}`;
    }

    if (equipmentType.includes('RIU')) {
      return `RIU-${normalizeRiuDisplay(topology)}`;
    }

    if (equipmentType.includes('DEU')) {
      return `DEU - ${normalizeDeuDisplay(topology)}`;
    }

    if (equipmentType.includes('PSU')) {
      return `PSU-${normalizePsuDisplay(topology)}`;
    }

    const route = normalizeRoute(topology);
    if (route) {
      const routeParts = route.split('.');
      for (let i = routeParts.length - 1; i >= 0; i--) {
        if (routeParts[i] !== '0') {
          return `${eq.equipmentType || 'DEVICE'}.${routeParts[i]}`;
        }
      }
    }

    return eq.equipmentType || 'Unknown';
  };

  const findMa6000G2Root = (targetSerial: string): IRemoteEquipment | undefined => {
    return allEquipment.find((eq) => {
      if (!belongsToTargetSerial(eq, targetSerial)) return false;
      const type = (eq.equipmentType || '').toUpperCase();
      if (!type.includes('DCU')) return false;
      const route = normalizeRoute(eq.topology);
      return route === '0.0.0.0' || route === '';
    });
  };

  const parseMA6000G2Path = (): TopologyPathSegment[] => {
    const path: TopologyPathSegment[] = [];
    const objectKey = device.objectKey;
    if (!objectKey) return path;

    const targetSerial = getTargetSerialFromObjectKey(objectKey);
    if (!targetSerial) return path;

    const rootDevice = findMa6000G2Root(targetSerial);
    path.push({
      label: buildMa6000G2NodeLabel(rootDevice || device, true),
      type: rootDevice?.equipmentType || 'DCU',
      serialNumber: rootDevice?.serialNumber || targetSerial,
      hostname: rootDevice?.hostname,
      status: rootDevice?.status || device.status,
      objectKey: rootDevice?.objectKey,
    });

    const currentType = (device.equipmentType || '').toUpperCase();
    const currentTopology = (device.topology || '').trim();
    const isSubDcu = currentType.includes('SDCU') || currentType.includes('S-DCU');
    const isSubRiu = currentType.includes('SRIU') || currentType.includes('S-RIU');
    const isSubPsu = currentType.includes('SPSU') || currentType.includes('S-PSU');

    const findParentSubDcu = (): IRemoteEquipment | undefined => {
      const byParentKey = allEquipment.find((eq) => {
        if (!belongsToTargetSerial(eq, targetSerial)) return false;
        const type = (eq.equipmentType || '').toUpperCase();
        if (!type.includes('SDCU') && !type.includes('S-DCU')) return false;
        return !!device.parentKey && eq.objectKey === device.parentKey;
      });

      if (byParentKey) return byParentKey;

      const currentRoute = normalizeRoute(currentTopology);
      const routeOctets = currentRoute.split('.');
      if (routeOctets.length !== 4) return undefined;

      const parentRoute = [...routeOctets];
      for (let i = parentRoute.length - 1; i >= 0; i--) {
        if (parentRoute[i] !== '0') {
          parentRoute[i] = '0';
          break;
        }
      }

      return allEquipment.find((eq) => {
        if (!belongsToTargetSerial(eq, targetSerial)) return false;
        const type = (eq.equipmentType || '').toUpperCase();
        if (!type.includes('SDCU') && !type.includes('S-DCU')) return false;
        return normalizeRoute(eq.topology) === parentRoute.join('.');
      });
    };

    const appendCurrentDevice = () => {
      path.push({
        label: buildMa6000G2NodeLabel(device),
        type: device.equipmentType || 'Device',
        serialNumber: device.serialNumber,
        hostname: device.hostname,
        status: device.status,
        objectKey: device.objectKey,
      });
    };

    if (currentType.includes('DCU')) {
      if (isSubDcu) {
        appendCurrentDevice();
      }
      return path;
    }

    if (currentType.includes('DXRU')) {
      const { deuKey } = parseDxruTopology(currentTopology);
      if (deuKey) {
        const parentDeu = allEquipment.find((eq) => {
          if (!belongsToTargetSerial(eq, targetSerial)) return false;
          const type = (eq.equipmentType || '').toUpperCase();
          if (!type.includes('DEU')) return false;
          return (eq.topology || '').trim().toUpperCase() === deuKey.toUpperCase();
        });

        path.push({
          label: parentDeu ? buildMa6000G2NodeLabel(parentDeu) : `DEU - ${normalizeDeuDisplay(deuKey)}`,
          type: parentDeu?.equipmentType || 'DEU',
          serialNumber: parentDeu?.serialNumber,
          hostname: parentDeu?.hostname,
          status: parentDeu?.status || device.status,
          objectKey: parentDeu?.objectKey,
        });
      }

      path.push({
        label: buildMa6000G2NodeLabel(device),
        type: device.equipmentType || 'Device',
        serialNumber: device.serialNumber,
        hostname: device.hostname,
        status: device.status,
        objectKey: device.objectKey,
      });

      return path;
    }

    if (currentType.includes('DEU') || currentType.includes('RIU') || currentType.includes('PSU')) {
      if (isSubRiu || isSubPsu) {
        const parentSubDcu = findParentSubDcu();
        if (parentSubDcu) {
          path.push({
            label: buildMa6000G2NodeLabel(parentSubDcu),
            type: parentSubDcu.equipmentType || 'sDCU',
            serialNumber: parentSubDcu.serialNumber,
            hostname: parentSubDcu.hostname,
            status: parentSubDcu.status || device.status,
            objectKey: parentSubDcu.objectKey,
          });
        }
      }

      appendCurrentDevice();
      return path;
    }

    const route = normalizeRoute(currentTopology);
    const routeOctets = route.split('.');
    if (routeOctets.length !== 4) {
      appendCurrentDevice();
      return path;
    }

    let currentRoute = '0.0.0.0';
    let currentDeviceAppended = false;

    for (let i = 0; i < 4; i++) {
      const octet = routeOctets[i];
      if (octet === '0') continue;

      const routeParts = currentRoute.split('.');
      routeParts[i] = octet;
      currentRoute = routeParts.join('.');

      const isCurrentDevice = currentRoute === route;

      if (isCurrentDevice) {
        appendCurrentDevice();
        currentDeviceAppended = true;
      } else {
        const intermediateDevice = allEquipment.find((eq) =>
          belongsToTargetSerial(eq, targetSerial) && normalizeRoute(eq.topology) === currentRoute
        );

        if (intermediateDevice) {
          path.push({
            label: buildMa6000G2NodeLabel(intermediateDevice),
            type: intermediateDevice.equipmentType || 'Device',
            serialNumber: intermediateDevice.serialNumber,
            hostname: intermediateDevice.hostname,
            status: intermediateDevice.status || device.status,
            objectKey: intermediateDevice.objectKey,
          });
        } else {
          path.push({
            label: `DEVICE.${octet}`,
            type: 'Device',
            status: device.status,
            objectKey: `${targetSerial}.*.*`,
          });
        }
      }
    }

    if (!currentDeviceAppended) {
      appendCurrentDevice();
    }

    return path;
  };

  /**
   * Parse MA6200-G1 route-based topology path
   * Format: {targetSerial}_{devId}_{route}
   * Example: SN123ABC_0_5.2.3.0
   * Route hierarchy: 0.0.0.0 (root) → 5.0.0.0 → 5.2.0.0 → 5.2.3.0
   */
  // ==============================
  // MA6200-G1
  // ==============================
  const findMa6200G1Root = (targetSerial: string): IRemoteEquipment | undefined => {
    return allEquipment.find((eq) =>
      belongsToTargetSerial(eq, targetSerial) && normalizeRoute(eq.topology) === '0.0.0.0'
    );
  };

  const parseMA6200G1Path = (): TopologyPathSegment[] => {
    const path: TopologyPathSegment[] = [];
    const objectKey = device.objectKey;
    
    if (!objectKey) return path;
    
    const targetSerial = getTargetSerialFromObjectKey(objectKey);
    if (!targetSerial) return path;

    const route = normalizeRoute(device.topology);
    if (!route) return path;

    // Detect G1 vs G2 by looking at the root device's model or equipment type
    const rootDevice = findMa6200G1Root(targetSerial);
    const isG2 = rootDevice?.model?.toUpperCase().includes('MA6200-G2') ||
                 rootDevice?.model?.toUpperCase().includes('MA6200G2') ||
                 rootDevice?.equipmentType?.toUpperCase() === 'A3' ||
                 device.model?.toUpperCase().includes('MA6200-G2') ||
                 device.model?.toUpperCase().includes('MA6200G2');
    const platform: 'g1' | 'g2' = isG2 ? 'g2' : 'g1';

    // Add root node
    path.push({
      label: buildMa6200NodeLabel('', '0', true, platform),
      type: rootDevice?.equipmentType || (isG2 ? 'A3' : 'AU2'),
      serialNumber: rootDevice?.serialNumber || targetSerial,
      hostname: rootDevice?.hostname,
      status: rootDevice?.status || device.status,
      objectKey: rootDevice?.objectKey,
    });

    // If this IS the root device, return root-only path
    if (route === '0.0.0.0') return path;
    
    // Build intermediate path segments based on route
    const routeOctets = route.split('.');
    if (routeOctets.length !== 4) return path;
    
    let currentRoute = '0.0.0.0';
    
    for (let i = 0; i < 4; i++) {
      const octet = routeOctets[i];
      if (octet !== '0') {
        const routeParts = currentRoute.split('.');
        routeParts[i] = octet;
        currentRoute = routeParts.join('.');
        
        const isCurrentDevice = currentRoute === route;
        
        if (isCurrentDevice) {
          path.push({
            label: buildMa6200NodeLabel(device.equipmentType || '', octet, false, platform),
            type: device.equipmentType || 'Device',
            serialNumber: device.serialNumber,
            hostname: device.hostname,
            status: device.status,
            objectKey: device.objectKey,
          });
        } else {
          const intermediateDevice = allEquipment.find(eq => 
            belongsToTargetSerial(eq, targetSerial) && normalizeRoute(eq.topology) === currentRoute
          );
          
          if (intermediateDevice) {
            path.push({
              label: buildMa6200NodeLabel(intermediateDevice.equipmentType || '', octet, false, platform),
              type: intermediateDevice.equipmentType || 'Device',
              serialNumber: intermediateDevice.serialNumber,
              hostname: intermediateDevice.hostname,
              status: intermediateDevice.status || device.status,
              objectKey: intermediateDevice.objectKey,
            });
          } else {
            // Placeholder when device not found in inventory
            path.push({
              label: `DEVICE.${octet}`,
              type: 'Device',
              status: device.status,
              objectKey: `${targetSerial}.*.*`,
            });
          }
        }
      }
    }
    
    return path;
  };

  // ==============================
  // MA3000 helpers
  // ==============================

  const buildMa3000NodeLabel = (
    equipmentType: string,
    index?: string | number,
    isRoot: boolean = false
  ): string => {
    if (isRoot) return 'MA-3000';
    if (index === undefined || index === null || `${index}`.trim() === '') {
      return equipmentType || 'Device';
    }
    return `${equipmentType || 'Device'}-${index}`;
  };

  const findMa3000Root = (siteSn: string): IRemoteEquipment | undefined => {
    return allEquipment.find((eq) => {
      if (!eq.objectKey?.startsWith(`${siteSn}.`)) return false;
      const eqType = (eq.equipmentType || '').toUpperCase();
      const eqTopology = (eq.topology || '').trim();
      return eqType === 'HCM' || eq.objectKey?.toLowerCase().includes('.hcm.') || eqTopology === '0.0.0.0';
    });
  };

  /**
   * Determine module type based on device hierarchy
   */
  const getModuleType = (
    equipmentType?: string,
    slot?: number,
    port?: number,
    remote?: number
  ): string => {
    if (remote !== undefined) {
      // For nested remotes (like RXU under RAU), parent module is typically OIM or RIM
      return port !== undefined ? "OIM" : "Module";
    }

    if (port !== undefined) {
      // Direct remotes are typically under OIM, RIM, or FCM
      if (equipmentType === "RAU" || equipmentType === "MRU") {
        return slot && slot <= 4 ? "RIM" : "OIM";
      }
      if (equipmentType === "RXU" || equipmentType === "MXU") {
        return "OIM";
      }
    }

    return "Module";
  };

  /**
   * Determine parent remote type for nested remotes
   */
  const getParentRemoteType = (equipmentType?: string): string => {
    if (equipmentType === "RXU" || equipmentType === "RXU67") {
      return "RAU";
    }
    if (equipmentType === "MXU") {
      return "MRU";
    }
    return "Remote";
  };

  /**
  * Parse MA3000 path from objectKey + topology.
  * objectKey: {controllerSerial}.{deviceType}.{deviceSerial}
  * topology: {chassisIndex}.{slot}.{port}.{remote}
   */
  // ==============================
  // MA3000
  // ==============================
  const parseMA3000Path = (): TopologyPathSegment[] => {
    const path: TopologyPathSegment[] = [];
    
    const objectKey = device.objectKey;
    if (!objectKey) {
      return path;
    }

    const parts = objectKey.split('.');
    if (parts.length < 3) {
      return path;
    }

    const siteSn = parts[0];
    if (!device.topology) {
      return path;
    }

    // Always start MA3000 path from root controller.
    const rootDevice = findMa3000Root(siteSn);

    path.push({
      label: buildMa3000NodeLabel('MA-3000', undefined, true),
      type: rootDevice?.equipmentType || 'HCM',
      serialNumber: rootDevice?.serialNumber || siteSn,
      hostname: rootDevice?.hostname,
      status: rootDevice?.status || device.status,
      objectKey: rootDevice?.objectKey,
    });

    const currentEqType = (device.equipmentType || '').toUpperCase();
    const currentObjectKey = (device.objectKey || '').toLowerCase();
    const currentTopology = normalizeRoute(device.topology);
    const isCurrentRootDevice =
      currentEqType === 'HCM' ||
      currentObjectKey.includes('.hcm.') ||
      currentTopology === '0.0.0.0' ||
      (!!rootDevice?.objectKey && rootDevice.objectKey === device.objectKey);

    if (isCurrentRootDevice) {
      return path;
    }

    const topologyParts = device.topology.split('.');
    if (topologyParts.length !== 4) {
      return path;
    }

    const chassisIndex = topologyParts[0];
    const slotIndex = parseInt(topologyParts[1]);
    const portIndex = parseInt(topologyParts[2]);
    const remoteIndex = parseInt(topologyParts[3]);

    // Get chassis serial from attributes (fallback)
    const chassisSerialNumber = parsedAttributes.chassisSerial;

    // Use chassisTypeName prop - if not available, skip adding chassis node
    // Only show chassis if we have a valid type (HEU/IHU/OIU)
    if (chassisTypeName && chassisTypeName !== 'Chassis') {
      // Look up ACM device for chassis details
      // ACM objectKey format: {siteSn}.acm.{chassisIndex}.0.0.0
      // Match by siteSn and chassisIndex
      const acmObjectKeyPrefix = `${siteSn}.`;
      
      //Strategy 1: Find ACM with matching siteSn and chassisIndex
      let acmDevice = allEquipment.find(
        eq => eq.equipmentType === 'ACM' && 
            eq.objectKey?.startsWith(acmObjectKeyPrefix) &&
            eq.topology?.split('.')?.[0] === String(chassisIndex)
      );
      
      path.push({
        label: buildMa3000NodeLabel(chassisTypeName, chassisIndex),
        type: chassisTypeName,
        serialNumber: acmDevice?.serialNumber || chassisSerialNumber,
        hostname: acmDevice?.hostname,
        status: device.status,
        objectKey: acmDevice?.objectKey,
      });
    }

    // Add module (slot) if present and not slot 0
    if (slotIndex !== undefined && slotIndex !== 0) {
      // Determine if current device IS the module
      const isCurrentDeviceModule = portIndex === 0 && remoteIndex === 0 &&
                                     ['OIM', 'RIM', 'FCM', 'FMM', 'FRM'].includes(device.equipmentType || '');
      
      if (isCurrentDeviceModule) {
        // Current device is the module - use its info directly
        path.push({
          label: buildMa3000NodeLabel(device.equipmentType || 'Module', slotIndex),
          type: device.equipmentType || 'Module',
          serialNumber: device.serialNumber,
          hostname: device.hostname,
          status: device.status,
          objectKey: device.objectKey,
        });
      } else {
        // For remote devices, look up the parent module
        // Module objectKey format: {siteSn}.{moduleType}.{chassisIndex}.{slotIndex}.0.0
        const moduleObjectKeyPrefix = `${siteSn}.`;
        
        // Try to find matching module by siteSn, chassisIndex, and slotIndex
        let moduleDevice = allEquipment.find(
          eq => ['OIM', 'RIM', 'FCM', 'FMM', 'FRM'].includes(eq.equipmentType || '') &&
                eq.objectKey?.startsWith(moduleObjectKeyPrefix) &&
                eq.topology?.split('.')?.[0] === String(chassisIndex) &&
                eq.topology?.split('.')?.[1] === String(slotIndex)
        );
        
        // If found, use actual module type; otherwise guess based on device type
        const moduleType = moduleDevice?.equipmentType || 
                          getModuleType(device.equipmentType, slotIndex, portIndex, remoteIndex);
        
        path.push({
          label: buildMa3000NodeLabel(moduleType, slotIndex),
          type: moduleType,
          serialNumber: moduleDevice?.serialNumber,
          hostname: moduleDevice?.hostname,
          status: device.status,
          objectKey: moduleDevice?.objectKey,
        });
      }
    }

    // Add remote (port) if present
    if (portIndex !== undefined && portIndex !== 0 && remoteIndex === 0) {
      // This is the remote device (RAU, MRU, etc.) - use current device
      path.push({
        label: buildMa3000NodeLabel(device.equipmentType || 'Remote', portIndex),
        type: device.equipmentType || "Remote",
        serialNumber: device.serialNumber,
        hostname: device.hostname,
        status: device.status,
        objectKey: device.objectKey,
      });
    }

    // If both port and remote are present, show intermediate remote
    if (portIndex !== undefined && portIndex !== 0 && remoteIndex !== undefined && remoteIndex !== 0) {
      // Determine parent remote type (typically RAU for RXU)
      const parentRemoteType = getParentRemoteType(device.equipmentType);
      // Parent objectKey format: {siteSn}.{parentRemoteType}.{chassisIndex}.{slotIndex}.{portIndex}.0
            const parentObjectKeyPrefix = `${siteSn}.`;
      const parentDevice = allEquipment.find(
        eq => eq.equipmentType === parentRemoteType && 
              eq.objectKey?.startsWith(parentObjectKeyPrefix) &&
              eq.topology === `${chassisIndex}.${slotIndex}.${portIndex}.0`
      );
      
      path.push({
        label: buildMa3000NodeLabel(parentRemoteType, portIndex),
        type: parentRemoteType,
        serialNumber: parentDevice?.serialNumber,
        hostname: parentDevice?.hostname,
        status: device.status,
        objectKey: parentDevice?.objectKey,
      });
      
      // Add final remote (RXU, MXU, etc.) - use current device
      path.push({
        label: buildMa3000NodeLabel(device.equipmentType || 'Device'),
        type: device.equipmentType || "Device",
        serialNumber: device.serialNumber,
        hostname: device.hostname,
        status: device.status,
        objectKey: device.objectKey,
      });
    }

    return path;
  };

  /**
   * Parse topology path based on platform
   */
  const parseTopologyPath = (): TopologyPathSegment[] => {
    const platform = detectPlatform();

    switch (platform) {
      case 'ma6200g2':
        return parseMA6200G2Path();
      case 'ma6000g2':
        return parseMA6000G2Path();
      case 'ma6200g1':
        return parseMA6200G1Path();
      case 'ma3000':
        return parseMA3000Path();
      default:
        return [];
    }
  };

  const pathSegments = parseTopologyPath();

  // Only show topology for remote devices with a valid path
  if (pathSegments.length === 0) {
    return null;
  }

  // Calculate dimensions
  const boxWidth = 70;
  const boxHeight = 24;
  const boxGap = 30;
  const totalWidth = pathSegments.length * boxWidth + (pathSegments.length - 1) * boxGap;
  const svgHeight = 40;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        marginTop: 0,
      }}
    >
      <svg
        width={totalWidth}
        height={svgHeight}
        style={{
          overflow: "visible",
        }}
      >
        {pathSegments.map((segment, index) => {
          const x = index * (boxWidth + boxGap);
          const isLast = index === pathSegments.length - 1;
          const isSelected = !!selectedObjectKey && segment.objectKey === selectedObjectKey;
          const isActive = isSelected || (!selectedObjectKey && isLast);
          const isClickable = !!onSegmentClick
            && !!segment.objectKey
            && !segment.objectKey.includes('*');
          const statusColor = getStatusColor(segment.status);

          return (
            <React.Fragment key={index}>
              {/* Connection line to next box */}
              {index < pathSegments.length - 1 && (
                <line
                  x1={x + boxWidth}
                  y1={svgHeight / 2}
                  x2={x + boxWidth + boxGap}
                  y2={svgHeight / 2}
                  stroke="#64748b"
                  strokeWidth={1.5}
                  markerEnd="url(#arrowhead)"
                />
              )}

              {/* Node box group */}
              <Tooltip
                title={
                  <div style={{ fontSize: '12px' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                      {segment.hostname || segment.type}
                    </div>
                    {segment.serialNumber && (
                      <div>Serial: {segment.serialNumber}</div>
                    )}
                    <div style={{ fontSize: '10px', opacity: 0.8, marginTop: '4px' }}>
                      {segment.objectKey || 'N/A'}
                    </div>
                  </div>
                }
                arrow
              >
                <g
                  onClick={isClickable ? () => onSegmentClick(segment.objectKey as string) : undefined}
                  style={{ cursor: isClickable ? 'pointer' : 'default' }}
                >
                  {/* Selection/hover highlight for last item */}
                  {isActive && (
                    <rect
                      x={x - 4}
                      y={svgHeight / 2 - boxHeight / 2 - 4}
                      width={boxWidth + 8}
                      height={boxHeight + 8}
                      rx={5}
                      fill="rgba(59, 130, 246, 0.15)"
                      stroke="none"
                    />
                  )}

                  {/* Main box */}
                  <rect
                    x={x}
                    y={svgHeight / 2 - boxHeight / 2}
                    width={boxWidth}
                    height={boxHeight}
                    rx={3}
                    fill="#334155"
                    stroke={isActive ? "#3b82f6" : "#64748b"}
                    strokeWidth={isActive ? 2 : 1.5}
                    opacity={isActive ? 1 : 0.9}
                  />

                  {/* Status indicator dot */}
                  <circle
                    cx={x + boxWidth - 6}
                    cy={svgHeight / 2 - boxHeight / 2 + 6}
                    r={3.5}
                    fill={statusColor}
                    stroke="#0f172a"
                    strokeWidth={1}
                  />

                  {/* Equipment label */}
                  <text
                    x={x + boxWidth / 2}
                    y={svgHeight / 2}
                    fontSize={9}
                    fontFamily="ui-sans-serif, system-ui, sans-serif"
                    fill="#e2e8f0"
                    fontWeight="600"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{ userSelect: "none" }}
                  >
                    {segment.label}
                  </text>
                </g>
              </Tooltip>
            </React.Fragment>
          );
        })}

        {/* Arrow marker definition */}
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="10"
            refX="8"
            refY="3"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <path d="M0,0 L0,6 L9,3 z" fill="#64748b" />
          </marker>
        </defs>
      </svg>
    </Box>
  );
};

export default DeviceTopologyPath;
